import { useEffect, useRef, useState } from "react";
import { Mic, Square, Paperclip, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDate } from "@/components/common";

function VoicePlayer({ fileId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/files/${fileId}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [fileId]);
  return src ? <audio controls src={src} className="mt-2 w-full" /> : null;
}

export default function NotesPanel({ contactId, files, reload }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [rec, setRec] = useState(null);
  const chunks = useRef([]);
  const load = () => api.get(`/contacts/${contactId}/notes`).then((r) => setNotes(r.data));
  useEffect(() => { load(); }, [contactId]); // eslint-disable-line

  const add = async () => {
    try { await api.post(`/contacts/${contactId}/notes`, { text }); setText(""); load(); reload(); setTimeout(reload, 7000); } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (blob, name, kind) => {
    const fd = new FormData();
    fd.append("file", blob, name);
    fd.append("kind", kind);
    try { await api.post(`/contacts/${contactId}/attachments`, fd); toast.success(kind === "voice" ? "Voice note saved" : "Attachment saved"); load(); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const startRec = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(s);
      chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = () => { s.getTracks().forEach((t) => t.stop()); const b = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" }); upload(b, `voice-${Date.now()}.${(mr.mimeType || "").includes("mp4") ? "m4a" : "webm"}`, "voice"); };
      mr.start();
      setRec(mr);
    } catch { toast.error("Microphone unavailable or permission denied. Type your note instead."); }
  };
  const del = async (id) => { await api.delete(`/notes/${id}`); load(); };
  const attachments = (files || []).filter((f) => f.kind === "attachment");

  return (
    <div className="space-y-4" data-testid="notes-panel">
      <textarea data-testid="note-textarea" rows={3} className="cd-textarea" placeholder="Write a note about this contact…" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <button data-testid="note-add-btn" disabled={!text.trim()} onClick={add} className="cd-btn-primary">Add note</button>
        {rec ? (
          <button data-testid="voice-stop-btn" onClick={() => { rec.stop(); setRec(null); }} className="cd-btn-danger"><Square className="h-4 w-4" />Stop & save</button>
        ) : (
          <button data-testid="voice-record-btn" onClick={startRec} className="cd-btn-secondary"><Mic className="h-4 w-4" />Voice note</button>
        )}
        <label className="cd-btn-secondary cursor-pointer"><Paperclip className="h-4 w-4" />Attach file
          <input data-testid="attachment-input" type="file" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], e.target.files[0].name, "attachment")} />
        </label>
      </div>
      <p className="text-xs text-slate-500">Voice transcription is not enabled — recordings are kept and you can add typed notes.</p>
      {attachments.length > 0 && (
        <div className="space-y-1">
          <p className="cd-label">Attachments</p>
          {attachments.map((f) => <a key={f.id} href={fileUrl(f.id, true)} className="flex items-center gap-2 text-sm text-teal-700"><Download className="h-4 w-4" />{f.filename}</a>)}
        </div>
      )}
      {notes.map((n) => (
        <div key={n.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`note-${n.id}`}>
          <div className="flex justify-between gap-2">
            <p className="whitespace-pre-wrap text-sm">{n.text}</p>
            <button onClick={() => del(n.id)} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
          </div>
          {n.kind === "voice" && <VoicePlayer fileId={n.file_id} />}
          <p className="mt-1 text-xs text-slate-400">{fmtDate(n.created_at)}</p>
        </div>
      ))}
    </div>
  );
}
